import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Switch, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Save } from "@mui/icons-material";
import { useAuth } from "../../hooks/AuthProvider";
import { AI_SETTINGS_ENDPOINT } from "../../config/urls";
import ButtonOne from "../../common/normal-button";
import { aiFetch, errorMessage } from "./aiApi";
import AIModelPicker from "./AIModelPicker";
import AICostHint from "./AICostHint";
import SectionCard, { FieldGroup } from "./SectionCard";

/**
 * Tenant-wide AI models: which keys pay (BotBridge or the tenant's own), the chat and
 * embedding models, and whether to fall back to BotBridge keys when the tenant's key
 * fails. The AI Bot and product-discovery nodes can override the chat model.
 */
export default function AISettingsPanel({ refreshKey = 0, onSaved, disabled = false }) {
  const { getValidToken } = useAuth();
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    const res = await aiFetch(getValidToken, AI_SETTINGS_ENDPOINT);
    if (res.ok) {
      setLoaded(res.data);
      setForm({
        credential_source: res.data.credential_source,
        chat_provider: res.data.chat_provider,
        chat_model: res.data.chat_model,
        embed_provider: res.data.embed_provider,
        embed_model: res.data.embed_model,
        fallback_to_platform: res.data.fallback_to_platform,
      });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const setSource = (source) =>
    setForm((f) => ({ ...f, credential_source: source, chat_provider: "", chat_model: "", embed_provider: "", embed_model: "" }));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const res = await aiFetch(getValidToken, AI_SETTINGS_ENDPOINT, { method: "PUT", body: form });
    setSaving(false);
    if (!res.ok) {
      setMessage({ type: "error", text: errorMessage(res.data) });
      return;
    }
    setLoaded(res.data);
    setMessage({ type: "success", text: "AI settings saved." });
    onSaved && onSaved(res.data);
  };

  const resolvedChat = loaded?.resolved?.chat;
  const resolvedEmbed = loaded?.resolved?.embed;
  // The model a picker currently points at: the unsaved choice, else what is in use.
  const pickedKey = (provider, model, resolved) =>
    provider && model ? `${provider}/${model}` : resolved ? `${resolved.provider}/${resolved.model}` : "";
  const inUse = (r) =>
    r?.display_name ? `In use: ${r.display_name} · ${r.provider_name} · ${r.source === "own" ? "your key" : "BotBridge keys"}` : "";

  return (
    <SectionCard
      data-testid="ai-settings-panel"
      title="AI models"
      description="Used by the AI Bot and AI product discovery. A flow node or the AI Bot can pick its own chat model. Connect your own provider keys on the Integrations page."
      footer={
        <ButtonOne
          title={saving ? "Saving..." : "Save AI settings"}
          onClick={save}
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          disabled={!form || saving || disabled}
        />
      }
    >
      {!form ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {loaded?.fallback_active_since && (
            <Alert severity="warning">
              Using BotBridge keys because your {loaded.fallback_provider_name} key failed (since{" "}
              {new Date(loaded.fallback_active_since).toLocaleString()}). Fix or reconnect the key on the Integrations
              page to switch back.
            </Alert>
          )}
          {[resolvedChat, resolvedEmbed].some((r) => r?.model_unavailable) && (
            <Alert severity="info">
              A selected model is no longer available, so its provider&apos;s default model is being used. Choose another
              model and save.
            </Alert>
          )}
          {message && <Alert severity={message.type}>{message.text}</Alert>}

          <FieldGroup title="Keys" description="Pay for AI with BotBridge's keys, or with your own provider account.">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={form.credential_source}
              disabled={disabled}
              onChange={(e, next) => next && setSource(next)}
            >
              <ToggleButton value="platform">BotBridge keys</ToggleButton>
              <ToggleButton value="own">My own key</ToggleButton>
            </ToggleButtonGroup>
          </FieldGroup>

          <FieldGroup title="Chat model" description={inUse(resolvedChat)}>
            <AIModelPicker
              capability="chat"
              hideSource
              source={form.credential_source}
              allowPlatformDefault={form.credential_source === "platform"}
              platformDefaultLabel="BotBridge default"
              disabled={disabled}
              value={{ source: form.credential_source, provider: form.chat_provider, model: form.chat_model }}
              onChange={(v) => setForm((f) => ({ ...f, chat_provider: v?.provider || "", chat_model: v?.model || "" }))}
            />
            <AICostHint
              kind="chat"
              chatModel={pickedKey(form.chat_provider, form.chat_model, resolvedChat)}
              embeddingModel={pickedKey(form.embed_provider, form.embed_model, resolvedEmbed)}
            />
          </FieldGroup>

          <FieldGroup
            title="Embedding model"
            description={`Turns the AI Bot's knowledge base into searchable vectors. Anthropic and DeepSeek don't offer embeddings. Changing it means the knowledge base must be re-embedded. ${inUse(resolvedEmbed)}`}
          >
            <AIModelPicker
              capability="embed"
              hideSource
              source={form.credential_source}
              allowPlatformDefault
              platformDefaultLabel="BotBridge default embeddings"
              disabled={disabled}
              value={{ source: form.credential_source, provider: form.embed_provider, model: form.embed_model }}
              onChange={(v) => setForm((f) => ({ ...f, embed_provider: v?.provider || "", embed_model: v?.model || "" }))}
            />
            <AICostHint
              kind="embed"
              chatModel={pickedKey(form.chat_provider, form.chat_model, resolvedChat)}
              embeddingModel={pickedKey(form.embed_provider, form.embed_model, resolvedEmbed)}
            />
          </FieldGroup>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid #eceff1",
              borderRadius: "12px",
              px: 2.5,
              py: 2,
            }}
          >
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>Fall back to BotBridge keys</Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "#888", mt: 0.25 }}>
                If your own key fails (expired, revoked or out of credits), keep answering customers on BotBridge keys.
              </Typography>
            </Box>
            <Switch
              checked={!!form.fallback_to_platform}
              disabled={disabled}
              inputProps={{ "aria-label": "Fall back to BotBridge keys" }}
              onChange={(e) => setForm((f) => ({ ...f, fallback_to_platform: e.target.checked }))}
            />
          </Box>
        </>
      )}
    </SectionCard>
  );
}
